// src/utils/date.ts
function formatDate(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(locale);
}
function formatDateTime(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleString(locale);
}
function formatTime(date, locale = "nl-BE") {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}
function getTimeOfDayGreeting(hour) {
  const h = hour ?? (/* @__PURE__ */ new Date()).getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  if (h >= 17 && h < 21) return "evening";
  return "night";
}
function formatDuration(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

// src/utils/timeoutSignal.ts
function timeoutSignal(ms) {
  const native = AbortSignal.timeout;
  if (typeof native === "function") return native.call(AbortSignal, ms);
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

// src/utils/format.ts
function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}
function truncateText(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}
function capitalize(str2) {
  if (!str2) return "";
  return str2.charAt(0).toUpperCase() + str2.slice(1);
}

// src/utils/tts.ts
function resolveLang(explicit) {
  if (explicit) return explicit;
  if (typeof document !== "undefined") {
    const htmlLang = document.documentElement?.lang;
    if (htmlLang) return htmlLang;
  }
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return "en-US";
}
function speak(text, opts = {}) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      reject(new Error("SPEECH_SYNTHESIS_UNAVAILABLE"));
      return;
    }
    let utterance;
    try {
      window.speechSynthesis.cancel();
      utterance = new SpeechSynthesisUtterance(text);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("SPEECH_SYNTHESIS_CONSTRUCT_FAILED"));
      return;
    }
    utterance.lang = resolveLang(opts.lang);
    utterance.rate = opts.rate ?? 0.9;
    utterance.volume = opts.volume ?? 1;
    utterance.pitch = opts.pitch ?? 1;
    try {
      const voices = window.speechSynthesis.getVoices?.();
      if (voices && voices.length > 0) {
        const prefix = utterance.lang.split("-")[0]?.toLowerCase();
        const hasMatch = voices.some((v) => v.lang?.toLowerCase().startsWith(prefix ?? ""));
        if (!hasMatch) {
          console.warn(
            `[caro/utils/tts] No voice installed for lang "${utterance.lang}"; browser will pick a default voice.`
          );
        }
      }
    } catch {
    }
    utterance.onend = () => resolve();
    utterance.onerror = (event) => {
      const errCode = event?.error ?? "SPEECH_SYNTHESIS_ERROR";
      reject(new Error(`SPEECH_SYNTHESIS_ERROR:${errCode}`));
    };
    try {
      window.speechSynthesis.speak(utterance);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("SPEECH_SYNTHESIS_SPEAK_FAILED"));
    }
  });
}
var cachedAudioContext = null;
function getAudioContextCtor() {
  if (typeof window === "undefined") return null;
  const w = window;
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}
function getAudioContext() {
  if (cachedAudioContext && cachedAudioContext.state !== "closed") {
    return cachedAudioContext;
  }
  const Ctor = getAudioContextCtor();
  if (!Ctor) return null;
  try {
    cachedAudioContext = new Ctor();
    return cachedAudioContext;
  } catch {
    cachedAudioContext = null;
    return null;
  }
}
function beep(opts = {}) {
  return new Promise((resolve, reject) => {
    const ctx = getAudioContext();
    if (!ctx) {
      reject(new Error("AUDIO_CONTEXT_UNAVAILABLE"));
      return;
    }
    const frequency = opts.frequency ?? 880;
    const durationMs = opts.durationMs ?? 200;
    const peakVolume = opts.volume ?? 0.4;
    const attackSec = 0.02;
    const releaseSec = 0.05;
    const durationSec = durationMs / 1e3;
    const sustainSec = Math.max(durationSec - attackSec - releaseSec, 0.01);
    const totalSec = attackSec + sustainSec + releaseSec;
    let oscillator;
    let gain;
    try {
      oscillator = ctx.createOscillator();
      gain = ctx.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = frequency;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peakVolume, now + attackSec);
      gain.gain.setValueAtTime(peakVolume, now + attackSec + sustainSec);
      gain.gain.linearRampToValueAtTime(0, now + totalSec);
      oscillator.start(now);
      oscillator.stop(now + totalSec);
    } catch (err) {
      reject(err instanceof Error ? err : new Error("AUDIO_BEEP_FAILED"));
      return;
    }
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      try {
        oscillator.disconnect();
        gain.disconnect();
      } catch {
      }
      resolve();
    };
    oscillator.onended = settle;
    setTimeout(settle, totalSec * 1e3 + 100);
  });
}
async function speakOrBeep(text, opts) {
  try {
    await speak(text, opts);
    return "spoken";
  } catch {
    try {
      await beep();
      return "beeped";
    } catch {
      return "failed";
    }
  }
}
function __resetAudioContextForTests() {
  if (cachedAudioContext) {
    try {
      void cachedAudioContext.close();
    } catch {
    }
  }
  cachedAudioContext = null;
}

// src/utils/appLanguage.ts
var APP_LANGUAGES = ["nl", "en", "fr", "de"];
var DEFAULT_APP_LANGUAGE = "nl";
var TEXT_FALLBACK_ORDER = ["nl", "fr", "en", "de"];
function isAppLanguage(value) {
  return typeof value === "string" && APP_LANGUAGES.includes(value);
}
function resolveAppLanguage(personLang, siteLang) {
  if (isAppLanguage(personLang)) return personLang;
  if (isAppLanguage(siteLang)) return siteLang;
  return DEFAULT_APP_LANGUAGE;
}
function hasTitle(value) {
  if (!value || typeof value !== "object") return false;
  const title = value.title;
  return typeof title === "string" && title.trim() !== "";
}
function pickLocalizedText(base, translations, opts) {
  const { lang: rawBaseLang, ...baseText } = base;
  const baseLang = isAppLanguage(rawBaseLang) ? rawBaseLang : DEFAULT_APP_LANGUAGE;
  const table = {};
  if (translations && typeof translations === "object") {
    for (const l of APP_LANGUAGES) {
      const entry = translations[l];
      if (entry && typeof entry === "object" && !Array.isArray(entry)) {
        table[l] = entry;
      }
    }
  }
  const candidates = [opts.lang];
  if (isAppLanguage(opts.siteLanguage)) candidates.push(opts.siteLanguage);
  candidates.push(DEFAULT_APP_LANGUAGE, ...TEXT_FALLBACK_ORDER);
  for (const l of candidates) {
    if (l === baseLang && hasTitle(baseText)) return { text: baseText, lang: l };
    if (hasTitle(table[l])) return { text: { ...table[l] }, lang: l };
  }
  return { text: baseText, lang: baseLang };
}

// src/utils/locationData.ts
var HOISTED_KEYS = {
  phoneNumber: "phoneNumber",
  r_sip_ext_tablet: "phoneNumber",
  emergencyIntegrationIdentifier: "emergencyIntegrationIdentifier",
  r_umo: "emergencyIntegrationIdentifier"
};
var FIELD_KEY_RE = /^\d+$/;
function isLocationDataField(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value) && typeof value.name === "string";
}
function parseLocationData(data) {
  let parsed = data;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }
  if (!parsed || typeof parsed !== "object") return null;
  return parsed;
}
function locationDataShape(data) {
  const parsed = parseLocationData(data);
  if (parsed == null) return "none";
  return Array.isArray(parsed) ? "array" : "object";
}
function listLocationDataFields(data) {
  const parsed = parseLocationData(data);
  if (!parsed) return [];
  if (Array.isArray(parsed)) {
    return parsed.map((entry, i) => ({ key: String(i), entry })).filter((f) => isLocationDataField(f.entry));
  }
  return Object.entries(parsed).filter(([key, value]) => FIELD_KEY_RE.test(key) && isLocationDataField(value)).map(([key, entry]) => ({ key, entry }));
}
function getLocationDataFields(data) {
  return listLocationDataFields(data).map((f) => f.entry);
}
function readLocationDataField(data, name) {
  const parsed = parseLocationData(data);
  if (!parsed) return null;
  const hit = listLocationDataFields(parsed).find((f) => f.entry.name === name);
  if (hit) return hit.entry.value === void 0 ? null : hit.entry.value;
  if (!Array.isArray(parsed) && HOISTED_KEYS[name]) {
    const hoisted = parsed[HOISTED_KEYS[name]];
    return hoisted != null && String(hoisted) !== "" ? hoisted : null;
  }
  return null;
}
function readLocationDataString(data, name) {
  const value = readLocationDataField(data, name);
  return value != null && String(value) !== "" ? String(value) : null;
}
function withLocationDataField(data, entry) {
  const parsed = parseLocationData(data);
  const { name } = entry;
  if (parsed == null || Array.isArray(parsed)) {
    const next = Array.isArray(parsed) ? [...parsed] : [];
    const idx = next.findIndex((e) => isLocationDataField(e) && e.name === name);
    if (idx >= 0) next[idx] = { ...next[idx], ...entry };
    else next.push({ ...entry });
    return next;
  }
  const out = { ...parsed };
  if (HOISTED_KEYS[name]) {
    out[HOISTED_KEYS[name]] = entry.value == null ? "" : String(entry.value);
    for (const f of listLocationDataFields(out)) if (f.entry.name === name) delete out[f.key];
    return out;
  }
  const fields = listLocationDataFields(out);
  const hit = fields.find((f) => f.entry.name === name);
  if (hit) {
    out[hit.key] = { ...out[hit.key], ...entry };
  } else {
    const max = fields.reduce((m, f) => Math.max(m, Number(f.key)), -1);
    out[String(max + 1)] = { ...entry };
  }
  return out;
}
function withoutLocationDataField(data, name) {
  const parsed = parseLocationData(data);
  if (parsed == null) return [];
  if (Array.isArray(parsed)) {
    return parsed.filter((e) => !(isLocationDataField(e) && e.name === name));
  }
  const out = { ...parsed };
  for (const f of listLocationDataFields(out)) if (f.entry.name === name) delete out[f.key];
  if (HOISTED_KEYS[name]) delete out[HOISTED_KEYS[name]];
  return out;
}
function replaceLocationDataFields(data, fields) {
  const entries = (Array.isArray(fields) ? fields : []).filter(isLocationDataField);
  const parsed = parseLocationData(data);
  if (parsed == null || Array.isArray(parsed)) return entries;
  const out = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (!(FIELD_KEY_RE.test(key) && isLocationDataField(value))) out[key] = value;
  }
  let i = 0;
  for (const entry of entries) {
    if (HOISTED_KEYS[entry.name]) {
      out[HOISTED_KEYS[entry.name]] = entry.value == null ? "" : String(entry.value);
      continue;
    }
    out[String(i++)] = entry;
  }
  return out;
}
function readLocationDefaultLanguage(data) {
  const parsed = parseLocationData(data);
  if (!parsed) return null;
  if (!Array.isArray(parsed) && isAppLanguage(parsed.defaultLanguage)) {
    return parsed.defaultLanguage;
  }
  const value = readLocationDataField(parsed, "defaultLanguage");
  return isAppLanguage(value) ? value : null;
}
var SITE_CUSTOM_FIELDS = {
  SITE_MANAGER_EMAIL: "site_manager_email",
  SITE_MANAGER_USER_ID: "site_manager_user_id",
  // Repair-estimates / invoicing custom fields (20260702-repair-estimates-catalog).
  // Read via `@caro/shared/utils/invoicing` helpers, mirroring getSiteManagerEmail.
  INVOICING_EMAIL: "invoicing_mail",
  // invoicing party's email (LD-INVOICE-MAIL)
  CLIENT_TAX_PROFILE: "tax_profile",
  // client tax-profile code (LD-TAX, CR-8)
  SERVICE_CONTRACT: "service_contract",
  // boolean flag: site under a service contract; gates Flow B (CR-12)
  // Per-site approval window in DAYS (2026-07-29). The 72h default is too short
  // for a client whose approval needs a board decision (a VME/syndicus meets
  // monthly); unset falls back to ESTIMATE_APPROVAL_DEFAULT_DAYS.
  ESTIMATE_APPROVAL_DAYS: "estimate_approval_days"
};
var ESTIMATE_APPROVAL_DEFAULT_DAYS = 3;
var ESTIMATE_APPROVAL_MIN_DAYS = 1;
var ESTIMATE_APPROVAL_MAX_DAYS = 90;

// src/utils/siteManager.ts
var LEGACY_RESPONSIBLE_EMAIL_FIELD = "responsibleEmail";
function getSiteManagerEmail(location) {
  const data = location?.data;
  return readLocationDataString(data, SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL) ?? readLocationDataString(data, LEGACY_RESPONSIBLE_EMAIL_FIELD);
}
function getSiteManagerUserId(location) {
  return readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID);
}
function siteHasManager(location) {
  return Boolean(getSiteManagerEmail(location));
}
function withSiteManagerFields(data, { email, userId }) {
  let next = withoutLocationDataField(data, SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL);
  next = withoutLocationDataField(next, SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID);
  if (!email) return next;
  next = withLocationDataField(next, {
    name: SITE_CUSTOM_FIELDS.SITE_MANAGER_EMAIL,
    value: email,
    type: "text",
    visible: true,
    permissionLevel: "authenticated"
  });
  return withLocationDataField(next, {
    name: SITE_CUSTOM_FIELDS.SITE_MANAGER_USER_ID,
    value: String(userId ?? ""),
    type: "text",
    visible: false,
    permissionLevel: "admin"
  });
}

// src/utils/invoicing.ts
function getInvoicingEmail(location) {
  return readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.INVOICING_EMAIL);
}
function getClientTaxProfile(location) {
  return readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE);
}
function isServiceContractSite(location) {
  const raw = readLocationDataField(location?.data, SITE_CUSTOM_FIELDS.SERVICE_CONTRACT);
  if (raw == null) return false;
  if (typeof raw === "boolean") return raw;
  const norm = String(raw ?? "").trim().toLowerCase();
  return norm === "true" || norm === "1" || norm === "yes" || norm === "on";
}
function normalizeApprovalDays(value) {
  const n = typeof value === "number" ? value : parseInt(String(value ?? "").trim(), 10);
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n);
  if (rounded < ESTIMATE_APPROVAL_MIN_DAYS || rounded > ESTIMATE_APPROVAL_MAX_DAYS) return null;
  return rounded;
}
function getEstimateApprovalDays(location) {
  const raw = readLocationDataString(location?.data, SITE_CUSTOM_FIELDS.ESTIMATE_APPROVAL_DAYS);
  return raw ? normalizeApprovalDays(raw) : null;
}
function resolveApprovalDays(location, override) {
  return normalizeApprovalDays(override) ?? getEstimateApprovalDays(location) ?? ESTIMATE_APPROVAL_DEFAULT_DAYS;
}
var INVOICING_FIELD_NAMES = [
  SITE_CUSTOM_FIELDS.INVOICING_EMAIL,
  SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE,
  SITE_CUSTOM_FIELDS.SERVICE_CONTRACT,
  SITE_CUSTOM_FIELDS.ESTIMATE_APPROVAL_DAYS
];
function normalizeSiteCustomData(data) {
  return getLocationDataFields(data);
}
function withInvoicingFields(data, {
  invoicingEmail,
  taxProfile,
  serviceContract,
  approvalDays
}) {
  let next = withoutLocationDataField(
    data,
    INVOICING_FIELD_NAMES[0]
  );
  for (const name of INVOICING_FIELD_NAMES.slice(1)) next = withoutLocationDataField(next, name);
  const field = (name, value) => ({
    name,
    value,
    type: "text",
    visible: true,
    permissionLevel: "admin"
  });
  const email = typeof invoicingEmail === "string" ? invoicingEmail.trim() : "";
  if (email) next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.INVOICING_EMAIL, email));
  const profile = typeof taxProfile === "string" ? taxProfile.trim() : "";
  if (profile) next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.CLIENT_TAX_PROFILE, profile));
  if (serviceContract) {
    next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.SERVICE_CONTRACT, "true"));
  }
  const days = normalizeApprovalDays(approvalDays);
  if (days !== null) {
    next = withLocationDataField(next, field(SITE_CUSTOM_FIELDS.ESTIMATE_APPROVAL_DAYS, String(days)));
  }
  return next;
}
function resolveLineTax(taxCategory, taxProfile, matrix) {
  const rows = Array.isArray(matrix) ? matrix : [];
  const match = rows.find(
    (r) => r?.tax_category === taxCategory && r?.tax_profile === taxProfile && r?.active !== false && r?.active !== 0
  );
  if (!match) {
    throw new Error(
      `resolveLineTax: no active tax_rate for (tax_category=${String(
        taxCategory
      )} \xD7 tax_profile=${String(taxProfile)}). Refusing to offer/finalize (fail-closed, NFR-5).`
    );
  }
  return {
    tax_rate_bps: match.rate_bps,
    legal_mention_key: match.legal_mention_key ?? null
  };
}
function availableTaxProfiles(matrix) {
  const rows = Array.isArray(matrix) ? matrix : [];
  const profiles = /* @__PURE__ */ new Set();
  for (const r of rows) {
    if (r?.tax_profile && r.active !== false && r.active !== 0) profiles.add(r.tax_profile);
  }
  return [...profiles].sort();
}
function computeLineTaxCents(unitPriceCents, quantity, taxRateBps) {
  return Math.round(unitPriceCents * quantity * taxRateBps / 1e4);
}

// src/utils/money.ts
function formatMoneyCents(cents, currency = "EUR") {
  if (cents === null || cents === void 0 || Number.isNaN(Number(cents))) return "\u2014";
  try {
    return new Intl.NumberFormat("nl-BE", {
      style: "currency",
      currency: currency || "EUR"
    }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency || "EUR"}`;
  }
}
function eurosToCents(input) {
  if (input === null || input === void 0 || input === "") return NaN;
  const n = Number(String(input).replace(",", "."));
  if (Number.isNaN(n)) return NaN;
  return Math.round(n * 100);
}
function formatBps(bps) {
  if (bps === null || bps === void 0 || Number.isNaN(Number(bps))) return "\u2014";
  const pct = Number(bps) / 100;
  return `${Number.isInteger(pct) ? pct : pct.toFixed(2)}%`;
}

// src/utils/workingDays.ts
var DEFAULT_WORKING_DAYS_TZ = "Europe/Brussels";
var SEND_WINDOW_START_HOUR = 8;
var SEND_WINDOW_END_HOUR = 18;
var BELGIAN_FIXED_HOLIDAYS = [
  { month: 1, day: 1, key: "nieuwjaar" },
  { month: 5, day: 1, key: "dag_van_de_arbeid" },
  { month: 7, day: 21, key: "nationale_feestdag" },
  { month: 8, day: 15, key: "olv_hemelvaart" },
  { month: 11, day: 1, key: "allerheiligen" },
  { month: 11, day: 11, key: "wapenstilstand" },
  { month: 12, day: 25, key: "kerstmis" }
];
var BELGIAN_EASTER_OFFSETS = [
  { offset: 1, key: "paasmaandag" },
  { offset: 39, key: "olh_hemelvaart" },
  { offset: 50, key: "pinkstermaandag" }
];
var pad2 = (n) => String(n).padStart(2, "0");
var ymdKey = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = (h + l - 7 * m + 114) % 31 + 1;
  return { year, month, day };
}
var holidayCache = /* @__PURE__ */ new Map();
function belgianHolidays(year) {
  const cached = holidayCache.get(year);
  if (cached) return cached.map((h) => ({ ...h }));
  const out = BELGIAN_FIXED_HOLIDAYS.map((h) => ({
    date: ymdKey(year, h.month, h.day),
    key: h.key
  }));
  const easter = easterSunday(year);
  for (const o of BELGIAN_EASTER_OFFSETS) {
    const t = new Date(Date.UTC(easter.year, easter.month - 1, easter.day + o.offset));
    out.push({ date: ymdKey(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate()), key: o.key });
  }
  out.sort((x, y) => x.date < y.date ? -1 : x.date > y.date ? 1 : 0);
  holidayCache.set(year, out);
  return out.map((h) => ({ ...h }));
}
function parseYmd(localDate) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(localDate));
  if (!match) throw new Error(`workingDays: expected YYYY-MM-DD, got "${localDate}"`);
  return { y: Number(match[1]), m: Number(match[2]), d: Number(match[3]) };
}
function isWorkingYmd(y, m, d) {
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  const key = ymdKey(y, m, d);
  return !belgianHolidays(y).some((h) => h.date === key);
}
function isBelgianWorkingDay(localDate) {
  const { y, m, d } = parseYmd(localDate);
  return isWorkingYmd(y, m, d);
}
var formatterCache = /* @__PURE__ */ new Map();
function formatterFor(tz) {
  let f = formatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
    formatterCache.set(tz, f);
  }
  return f;
}
function localParts(date, tz) {
  const parts = formatterFor(tz).formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return {
    y: get("year"),
    m: get("month"),
    d: get("day"),
    hh: get("hour") % 24,
    mm: get("minute"),
    ss: get("second"),
    ms: date.getUTCMilliseconds()
  };
}
function offsetAt(t, tz) {
  const p = localParts(new Date(t), tz);
  return Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss, p.ms) - t;
}
function zonedToUtc(p, tz) {
  const guess = Date.UTC(p.y, p.m - 1, p.d, p.hh, p.mm, p.ss, p.ms);
  const first = guess - offsetAt(guess, tz);
  const second = guess - offsetAt(first, tz);
  return new Date(second);
}
function nextWorkingYmd(y, m, d) {
  let t = new Date(Date.UTC(y, m - 1, d + 1));
  while (!isWorkingYmd(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate())) {
    t = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate() + 1));
  }
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}
function localDateKey(date, tz = DEFAULT_WORKING_DAYS_TZ) {
  const p = localParts(date, tz);
  return ymdKey(p.y, p.m, p.d);
}
function addWorkingDays(anchor, n, tz = DEFAULT_WORKING_DAYS_TZ) {
  const steps = Math.max(0, Math.floor(Number(n) || 0));
  let p = localParts(anchor, tz);
  if (!isWorkingYmd(p.y, p.m, p.d)) {
    const next = nextWorkingYmd(p.y, p.m, p.d);
    p = { ...next, hh: 0, mm: 0, ss: 0, ms: 0 };
  }
  let date = { y: p.y, m: p.m, d: p.d };
  for (let i = 0; i < steps; i++) date = nextWorkingYmd(date.y, date.m, date.d);
  return zonedToUtc({ ...p, ...date }, tz);
}
function addThreshold(anchor, amount, unit, tz = DEFAULT_WORKING_DAYS_TZ) {
  if (unit === "workdays") return addWorkingDays(anchor, amount, tz);
  return new Date(anchor.getTime() + Number(amount) * 3600 * 1e3);
}
function workingDaysBetween(a, b, tz = DEFAULT_WORKING_DAYS_TZ) {
  if (!(b.getTime() > a.getTime())) return 0;
  let n = 0;
  const maxSteps = Math.ceil((b.getTime() - a.getTime()) / 864e5) + 1;
  while (n < maxSteps && addWorkingDays(a, n + 1, tz).getTime() <= b.getTime()) n++;
  return n;
}
function isWithinSendWindow(now, tz = DEFAULT_WORKING_DAYS_TZ) {
  const p = localParts(now, tz);
  if (!isWorkingYmd(p.y, p.m, p.d)) return false;
  return p.hh >= SEND_WINDOW_START_HOUR && p.hh < SEND_WINDOW_END_HOUR;
}

// src/utils/peopleImport.ts
var PLAN_NUMBER_MAX = 32;
function normalizePlanNumber(raw) {
  if (raw === null || raw === void 0) return null;
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const s = String(raw).replace(/\s+/g, " ").trim();
  return s === "" ? null : s;
}
function validatePlanNumber(raw) {
  if (raw !== null && raw !== void 0 && typeof raw !== "string" && !(typeof raw === "number" && Number.isFinite(raw))) {
    return { ok: false, code: "PLAN_NUMBER_INVALID" };
  }
  const value = normalizePlanNumber(raw);
  if (value !== null && value.length > PLAN_NUMBER_MAX) {
    return { ok: false, code: "PLAN_NUMBER_TOO_LONG" };
  }
  return { ok: true, value };
}
function planNumberKey(raw) {
  const v = normalizePlanNumber(raw);
  return v === null ? null : v.toLowerCase();
}
var FALLBACK_OWNERSHIP_START = "2000-01-01";
var UNIT_MATCH_MIN_SCORE = 60;
var str = (raw) => raw === null || raw === void 0 ? "" : String(raw).replace(/\s+/g, " ").trim();
var stripZeros = (digits) => digits.replace(/^0+(?=\d)/, "");
function normalizeUnitNumber(raw) {
  const s = str(raw).toLowerCase().replace(/\s+/g, "").replace(/[/._-]+/g, "-").replace(/^-+|-+$/g, "");
  if (!s) return "";
  return s.split("-").flatMap((seg) => seg.match(/\d+|\D+/g) || []).map((x) => /^\d+$/.test(x) ? stripZeros(x) : x).join("-");
}
function parseUnitName(name) {
  const m = str(name).match(/^([A-Za-z]+\d*)-(\d+)-F(\d+)-(\d+)(?=$|\D)/i);
  if (!m) return null;
  return { site: m[1].toUpperCase(), house: Number(m[2]), floor: Number(m[3]), seq: Number(m[4]) };
}
function parseFloorSeq(raw) {
  const s = str(raw);
  if (!s) return null;
  let house = null;
  let body = s;
  const slash = s.lastIndexOf("/");
  if (slash >= 0) {
    const hm = s.slice(0, slash).match(/(\d+)\s*$/);
    house = hm ? Number(hm[1]) : null;
    body = s.slice(slash + 1);
  }
  const dotted = body.match(/(\d+)\s*\.\s*(\d+)/);
  if (dotted) return { floor: Number(dotted[1]), seq: Number(dotted[2]), house };
  const digits = body.match(/^\D*(\d{3,})\D*$/);
  if (digits) {
    const d = digits[1];
    return { floor: Number(d.slice(0, -2)), seq: Number(d.slice(-2)), house };
  }
  return null;
}
var lastNumber = (normalized) => {
  const nums = normalized.split("-").filter((x) => /^\d+$/.test(x));
  return nums.length ? nums[nums.length - 1] : null;
};
function scoreUnit(row, unit, matchColumn = "appNr") {
  const codeKey = planNumberKey(row.code);
  if (codeKey !== null && planNumberKey(unit.planNumber) === codeKey) return 100;
  const value = matchColumn === "code" ? row.code : row.appNr;
  const nv = normalizeUnitNumber(value);
  const nn = normalizeUnitNumber(unit.name);
  if (!nv || !nn) return 0;
  if (nn === nv) return 90;
  const pu = parseUnitName(unit.name);
  const pf = parseFloorSeq(value);
  if (pu && pf) {
    if (pu.floor === pf.floor && pu.seq === pf.seq) {
      return pf.house !== null && pf.house === pu.house ? 75 : 60;
    }
    return pu.seq === pf.seq ? 40 : 0;
  }
  const vs = nv.split("-");
  const ns = nn.split("-");
  if (vs.length >= 2 && ns.length >= vs.length && ns.slice(-vs.length).join("-") === nv) return 75;
  const lv = lastNumber(nv);
  return lv !== null && lv === lastNumber(nn) ? 40 : 0;
}
function matchUnits(rows, units, {
  matchColumn = "appNr",
  taken = []
} = {}) {
  const takenSet = new Set([...taken].map(Number));
  const out = rows.map((row) => {
    const candidates = units.map((u) => ({ unitId: Number(u.id), score: scoreUnit(row, u, matchColumn) })).filter((c) => c.score > 0).sort((a, b) => b.score - a.score || a.unitId - b.unitId);
    const free = candidates.filter((c) => !takenSet.has(c.unitId));
    const best = free[0];
    if (!best || best.score < UNIT_MATCH_MIN_SCORE) {
      return { unitId: null, score: best ? best.score : 0, ambiguous: false, candidates };
    }
    if (free[1] && free[1].score === best.score) {
      return { unitId: null, score: best.score, ambiguous: true, candidates };
    }
    return { unitId: best.unitId, score: best.score, ambiguous: false, candidates };
  });
  const byUnit = /* @__PURE__ */ new Map();
  out.forEach((m, i) => {
    if (m.unitId === null) return;
    byUnit.set(m.unitId, [...byUnit.get(m.unitId) || [], i]);
  });
  for (const idxs of byUnit.values()) {
    if (idxs.length < 2) continue;
    const top = Math.max(...idxs.map((i) => out[i].score));
    const winners = idxs.filter((i) => out[i].score === top);
    for (const i of idxs) {
      if (winners.length === 1 && i === winners[0]) continue;
      out[i] = { ...out[i], unitId: null, ambiguous: true };
    }
  }
  return out;
}
var CAPS_WORD = /^[A-ZÀ-ÖØ-Þ]{2,}\.?$/u;
var COUPLE_RE = /\s[-–+&]\s|&|\sen\s/i;
function splitName(raw) {
  let s = str(raw);
  const flags = [];
  if (/\(\s*overleden\s*\)/i.test(s)) {
    flags.push("deceased");
    s = s.replace(/\(\s*overleden\s*\)/gi, " ");
  }
  if (/\([^)]*\)?/.test(s)) {
    flags.push("parentheses");
    s = s.replace(/\([^)]*\)?/g, " ");
  }
  s = s.replace(/\s+/g, " ").trim();
  if (COUPLE_RE.test(s)) flags.push("couple");
  let words = s.split(" ").filter(Boolean);
  if (words.some((w) => CAPS_WORD.test(w))) {
    flags.push("caps");
    if (words.some((w) => !CAPS_WORD.test(w))) {
      while (words.length > 1 && CAPS_WORD.test(words[words.length - 1])) words = words.slice(0, -1);
    }
  }
  if (words.length <= 1) {
    flags.push("single_word");
    return { firstName: "", lastName: words[0] || "", flags };
  }
  const firstName = words[words.length - 1];
  const lastName = words.slice(0, -1).join(" ").replace(/[\s\-–+&]+$/u, "").trim();
  return { firstName, lastName, flags };
}
function sortForReview(rows, flagsOf = (r) => r.flags) {
  const doubt = (r) => (flagsOf(r) || []).length ? 0 : 1;
  return rows.map((r, i) => ({ r, i })).sort((a, b) => doubt(a.r) - doubt(b.r) || a.i - b.i).map((x) => x.r);
}
var LEGAL_RE = /(^|\s)(bv|nv|vzw|bvba|srl|sa|comm\.?\s?v)\.?(\s|$)/i;
function isLegalEntity(name) {
  return LEGAL_RE.test(str(name));
}
function ownerKindOf(name) {
  return isLegalEntity(name) ? "legal" : "natural";
}
function normalizeNameForCompare(...parts) {
  return parts.filter((p) => p !== null && p !== void 0 && p !== "").map((p) => String(p)).join(" ").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").split(/\s+/).filter(Boolean).sort().join(" ");
}
var EXPENSE_RE = /^EXPENSE\b/i;
function isExpenseCode(code) {
  return EXPENSE_RE.test(str(code));
}
function baseKind(part, residenceCode) {
  const s = part.toUpperCase();
  if (/^P\d+(?![.\d])/.test(s) || /^MVP\s?\d+/.test(s)) return "parking";
  if (/BERGING/.test(s)) return "storage";
  if (/^B\d+$/.test(s) && (!residenceCode || residenceCode === "BDL")) return "storage";
  if (/^K\d+$/.test(s) && (!residenceCode || residenceCode === "MOEIE")) return "storage";
  if (/BURO\s*WA\b/.test(s)) return "other";
  if (/BRASSERIE|KAPPER|WELLNESS|HANDELSPAND|RESTAURANT|HSCAB/.test(s)) return "commercial";
  return "apartment";
}
function kindFromCode(code, residenceCode = null) {
  const s = str(code);
  if (EXPENSE_RE.test(s)) return null;
  const mixed = /\+/.test(s) || /\bDUPLEX\b/i.test(s);
  const first = s.split("+")[0].replace(/\bDUPLEX\b/i, "").trim();
  const res = residenceCode ? str(residenceCode).toUpperCase() : null;
  return { kind: baseKind(first, res), mixed };
}
var DAY_MS = 864e5;
var MAX_EXCEL_SERIAL = 2958465;
function excelSerialToIsoDate(serial) {
  const n = typeof serial === "number" ? serial : Number.NaN;
  if (!Number.isFinite(n) || n < 1 || n > MAX_EXCEL_SERIAL) return null;
  const day = Math.floor(n);
  if (day === 60) return null;
  const base = day < 60 ? Date.UTC(1899, 11, 31) : Date.UTC(1899, 11, 30);
  return new Date(base + day * DAY_MS).toISOString().slice(0, 10);
}
var pad = (n) => String(n).padStart(2, "0");
function isoIfReal(y, m, d) {
  if (!(y >= 1e3 && y <= 9999 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}
function toIsoDate(raw) {
  if (raw === null || raw === void 0 || raw === "") return null;
  if (raw instanceof Date) {
    if (Number.isNaN(raw.getTime())) return null;
    return isoIfReal(raw.getUTCFullYear(), raw.getUTCMonth() + 1, raw.getUTCDate());
  }
  if (typeof raw === "number") return excelSerialToIsoDate(raw);
  const s = str(raw);
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/);
  if (m) return isoIfReal(Number(m[1]), Number(m[2]), Number(m[3]));
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (m) return isoIfReal(Number(m[3]), Number(m[2]), Number(m[1]));
  if (/^\d{5}(\.\d+)?$/.test(s)) return excelSerialToIsoDate(Number(s));
  return null;
}
function dossierStateOf({ startedOn = null, endedOn = null } = {}, today) {
  if (endedOn && endedOn < today) return "ended";
  if (startedOn && startedOn > today) return "future";
  return "current";
}
function dossierState(dossiers, today) {
  const list = Array.isArray(dossiers) ? dossiers : [];
  if (!list.length) return { occupancy: "uninhabited", reason: "no_dossier" };
  const states = list.map((d) => dossierStateOf(d, today));
  if (states.includes("current")) return { occupancy: "inhabited", reason: null };
  return { occupancy: "uninhabited", reason: states.includes("future") ? "future" : "ended" };
}
function ownershipStart({
  deedDate = null,
  compromiseDate = null,
  firstUseDate = null
} = {}) {
  if (deedDate) return { validFrom: deedDate, source: "deed", fallback: false };
  if (compromiseDate) return { validFrom: compromiseDate, source: "compromise", fallback: false };
  if (firstUseDate) return { validFrom: firstUseDate, source: "first_use", fallback: false };
  return { validFrom: FALLBACK_OWNERSHIP_START, source: "fallback", fallback: true };
}
var COUNTRY_NAMES = {
  belgie: "BE",
  belgique: "BE",
  belgium: "BE",
  belgien: "BE",
  bel: "BE",
  nederland: "NL",
  netherlands: "NL",
  "pays-bas": "NL",
  niederlande: "NL",
  frankrijk: "FR",
  france: "FR",
  frankreich: "FR",
  duitsland: "DE",
  allemagne: "DE",
  germany: "DE",
  deutschland: "DE",
  luxemburg: "LU",
  luxembourg: "LU",
  spanje: "ES",
  espagne: "ES",
  spain: "ES",
  espana: "ES",
  italie: "IT",
  italy: "IT",
  italia: "IT",
  zwitserland: "CH",
  suisse: "CH",
  switzerland: "CH",
  schweiz: "CH",
  portugal: "PT",
  "verenigd koninkrijk": "GB",
  "united kingdom": "GB",
  "royaume-uni": "GB"
};
function countryToIso(raw) {
  const s = str(raw).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
  if (!s) return { code: null, unknown: false };
  if (/^[a-z]{2}$/.test(s)) return { code: s.toUpperCase(), unknown: false };
  const hit = COUNTRY_NAMES[s];
  return hit ? { code: hit, unknown: false } : { code: null, unknown: true };
}

export { APP_LANGUAGES, BELGIAN_EASTER_OFFSETS, BELGIAN_FIXED_HOLIDAYS, DEFAULT_APP_LANGUAGE, DEFAULT_WORKING_DAYS_TZ, FALLBACK_OWNERSHIP_START, PLAN_NUMBER_MAX, SEND_WINDOW_END_HOUR, SEND_WINDOW_START_HOUR, TEXT_FALLBACK_ORDER, UNIT_MATCH_MIN_SCORE, __resetAudioContextForTests, addThreshold, addWorkingDays, availableTaxProfiles, beep, belgianHolidays, capitalize, computeLineTaxCents, countryToIso, dossierState, dossierStateOf, easterSunday, eurosToCents, excelSerialToIsoDate, formatBps, formatDate, formatDateTime, formatDuration, formatFileSize, formatMoneyCents, formatTime, getClientTaxProfile, getEstimateApprovalDays, getInvoicingEmail, getLocationDataFields, getSiteManagerEmail, getSiteManagerUserId, getTimeOfDayGreeting, isAppLanguage, isBelgianWorkingDay, isExpenseCode, isLegalEntity, isLocationDataField, isServiceContractSite, isWithinSendWindow, kindFromCode, listLocationDataFields, localDateKey, locationDataShape, matchUnits, normalizeApprovalDays, normalizeNameForCompare, normalizePlanNumber, normalizeSiteCustomData, normalizeUnitNumber, ownerKindOf, ownershipStart, parseFloorSeq, parseLocationData, parseUnitName, pickLocalizedText, planNumberKey, readLocationDataField, readLocationDataString, readLocationDefaultLanguage, replaceLocationDataFields, resolveAppLanguage, resolveApprovalDays, resolveLineTax, scoreUnit, siteHasManager, sortForReview, speak, speakOrBeep, splitName, timeoutSignal, toIsoDate, truncateText, validatePlanNumber, withInvoicingFields, withLocationDataField, withSiteManagerFields, withoutLocationDataField, workingDaysBetween };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map